import { AbstractControl, ValidationErrors } from '@angular/forms';

export class PasswordValidators {
  /**
   * Validator that checks if the control's value contains at least one uppercase letter (A-Z).
   *
   * @example
   * ```typescript
   * passwordControl.addValidators(hasUppercase);
   * ```
   *
   * @returns A validation error `{ missingUppercase: true }` if invalid, otherwise `null`.
   */
  static hasUppercase(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';
    return /[A-Z]/.test(value) ? null : { missingUppercase: true };
  }

  /**
   * Validator that checks if the control's value contains at least one lowercase letter (a-z).
   *
   * @example
   * ```typescript
   * passwordControl.addValidators(hasLowercase);
   * ```
   *
   * @returns A validation error `{ missingLowercase: true }` if invalid, otherwise `null`.
   */
  static hasLowercase(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';
    return /[a-z]/.test(value) ? null : { missingLowercase: true };
  }

  /**
   * Validator that checks if the control's value contains at least one special character.
   * Supported special characters: `!@#$%^&*()_+-=[]{}|;:,.<>?`
   *
   * @example
   * ```typescript
   * passwordControl.addValidators(hasSpecialChar);
   * ```
   *
   * @returns A validation error `{ missingSpecialChar: true }` if invalid, otherwise `null`.
   */
  static hasSpecialChar(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';
    return /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(value)
      ? null
      : { missingSpecialChar: true };
  }

  /**
   * Validator that checks if the control's value contains sequential characters,
   * such as "abc", "123", or "XYZ".
   *
   * Sequences are detected within:
   * - Lowercase alphabet (`abcdefghijklmnopqrstuvwxyz`)
   * - Uppercase alphabet (`ABCDEFGHIJKLMNOPQRSTUVWXYZ`)
   * - Digits (`0123456789`)
   *
   * @example
   * ```typescript
   * passwordControl.addValidators(noSequentialChars);
   * ```
   *
   * @returns A validation error `{ sequentialChars: true }` if a sequence is found, otherwise `null`.
   */
  static noSequentialChars(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';
    if (!value) return null;

    const sequences = [
      'abcdefghijklmnopqrstuvwxyz',
      'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
      '0123456789',
    ];

    for (const seq of sequences) {
      for (let i = 0; i < seq.length - 2; i++) {
        const sub = seq.substring(i, i + 3);
        if (value.includes(sub)) {
          return { sequentialChars: true };
        }
      }
    }

    return null;
  }
}
